import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ShieldCheck, MapPin, ArrowRight, Star, ExternalLink, MessageCircle, SlidersHorizontal, X } from "lucide-react";
import { useLang } from "@/context/LangContext";
import InlineAdBanner from "@/components/InlineAdBanner";
import AppCategoryChips from "./AppCategoryChips";

/**
 * Shared landing layout for the Apps mega-menu verticals.
 *
 * config:
 *  appId, name, tagline, ctaLabel
 *  hero:        { background, overlay, badge, searchPlaceholder }
 *  theme:       { primary (CSS hex/rgb), accent (CSS) }
 *  categories:  string[]            — server-side filter (anyOf)
 *  filterGroups (NEW):
 *    Array of { id, label, options: [{ value, label? }], multi: true }
 *    Renders as a right-side sidebar (or bottom sheet on mobile) with
 *    collapsible groups of multi-select checkboxes. Selections form an
 *    object { [groupId]: [value, ...] }. Filtering:
 *      • within a group  → OR    (vendor matches ANY selected option)
 *      • across groups   → AND   (every active group must match)
 *      • match basis      → substring (case-insensitive) against the
 *        vendor's business_name / description / tags / product names.
 */
export default function AppLanding({ config }) {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const city = params.get("city") || "";
  const search = params.get("search") || "";
  const chip = params.get("chip") || "";

  const [searchDraft, setSearchDraft] = useState(search);
  const [cities, setCities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({}); // { groupId: [values...] }

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data || [])); }, []);

  useEffect(() => {
    setLoading(true);
    const q = {
      categories: config.categories.join(","),
      page_size: 60,
      sort: "rating",
    };
    if (city) q.city = city;
    if (search) q.search = search;
    api.get("/vendors", { params: q })
      .then((r) => setVendors(r.data?.items || []))
      .finally(() => setLoading(false));
  }, [city, search, config.categories]);

  // Apply client-side filter groups + optional category chip
  const filtered = useMemo(() => {
    const activeGroups = Object.entries(selected).filter(([, vals]) => vals && vals.length > 0);
    const chipQuery = (config.chips || []).find((c) => c.id === chip)?.query || "";
    if (activeGroups.length === 0 && !chipQuery) return vendors;
    const haystack = (v) =>
      `${v.business_name || ""} ${v.description || ""} ${v.category || ""} ${(v.tags || []).join(" ")} ${(v.products || []).map((p) => `${p.name} ${p.description || ""}`).join(" ")}`.toLowerCase();
    return vendors.filter((v) => {
      const h = haystack(v);
      if (chipQuery && !h.includes(chipQuery.toLowerCase())) return false;
      // Across groups → AND; within group → OR
      return activeGroups.every(([, vals]) => vals.some((val) => h.includes(String(val).toLowerCase())));
    });
  }, [vendors, selected, chip, config.chips]);

  const updateParam = (key, val) => {
    const np = new URLSearchParams(params);
    if (val) np.set(key, val);
    else np.delete(key);
    setParams(np);
  };

  const toggleOption = (groupId, value) => {
    setSelected((s) => {
      const arr = s[groupId] || [];
      const next = arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value];
      return { ...s, [groupId]: next };
    });
  };

  const clearAllFilters = () => setSelected({});
  const activeCount = Object.values(selected).reduce((a, arr) => a + (arr?.length || 0), 0);

  const renderFiltersBody = () => (
    <Accordion type="multiple" defaultValue={(config.filterGroups || []).slice(0, 3).map((g) => g.id)} className="w-full">
      {(config.filterGroups || []).map((g) => {
        const groupSelected = selected[g.id] || [];
        return (
          <AccordionItem value={g.id} key={g.id} className="border-border/60">
            <AccordionTrigger className="text-sm py-3 hover:no-underline" data-testid={`app-${config.appId}-fg-${g.id}`}>
              <span className="flex-1 text-left">{g.label}</span>
              {groupSelected.length > 0 && (
                <Badge variant="secondary" className="mr-2 h-5 px-1.5 text-[10px]">{groupSelected.length}</Badge>
              )}
            </AccordionTrigger>
            <AccordionContent className="space-y-1.5 pb-3">
              {g.options.map((opt) => {
                const value = typeof opt === "string" ? opt : opt.value;
                const label = typeof opt === "string" ? opt : opt.label || opt.value;
                const checked = groupSelected.includes(value);
                return (
                  <label
                    key={value}
                    className="flex items-center gap-2 cursor-pointer text-sm px-1 py-0.5 rounded hover:bg-muted/70"
                    data-testid={`app-${config.appId}-opt-${g.id}-${value}`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggleOption(g.id, value)} />
                    <span className="flex-1">{label}</span>
                  </label>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );

  return (
    <div className="min-h-screen">
      {/* Hero — true full-bleed (gradient spans viewport edge-to-edge) */}
      <section
        className="relative overflow-hidden"
        style={{ background: config.hero.background }}
        data-testid={`app-${config.appId}-hero`}
      >
        <div className="absolute inset-0" style={{ background: config.hero.overlay || "rgba(0,0,0,0.25)" }} />
        <div className="relative mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-16 sm:py-20 text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-medium">
            {config.hero.badge}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t(`apps.${config.appId}.name`)}</h1>
          <p className="mt-2 max-w-xl text-sm sm:text-base opacity-90">{t(`apps.${config.appId}.tagline`)}</p>

          {/* Search bar */}
          <div className="mt-6 max-w-2xl bg-white rounded-2xl p-2 flex items-center gap-2 shadow-2xl shadow-black/20">
            <Input
              placeholder={config.hero.searchPlaceholder || t("vl.search_placeholder")}
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && updateParam("search", searchDraft)}
              className="border-0 focus-visible:ring-0 text-foreground h-11"
              data-testid={`app-${config.appId}-search`}
            />
            <Select value={city || "all"} onValueChange={(v) => updateParam("city", v === "all" ? "" : v)}>
              <SelectTrigger className="h-11 w-36 border-0 text-foreground bg-muted" data-testid={`app-${config.appId}-city`}>
                <SelectValue placeholder={t("vl.all_cities")} />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">{t("vl.all_cities")}</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              className="rounded-xl h-11 px-5"
              style={{ background: config.theme.primary, color: "#fff" }}
              onClick={() => updateParam("search", searchDraft)}
              data-testid={`app-${config.appId}-search-btn`}
            >
              {t(`apps.${config.appId}.cta`)}
            </Button>
          </div>
          {(config.chips || []).length > 0 && (
            <AppCategoryChips
              chips={config.chips}
              active={chip}
              onChange={(id) => updateParam("chip", id || "")}
              testIdPrefix={`app-${config.appId}`}
            />
          )}
        </div>
      </section>

      {/* City-targeted ad banner (slot `app-${appId}`) */}
      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 pt-6" data-testid={`app-${config.appId}-adslot`}>
        <InlineAdBanner slot={`app-${config.appId}`} city={city} />
      </div>

      {/* Body: 3-column on xl+  ·  left ad rail · grid (with inline ads) · right filter sidebar */}
      <section className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-12 py-10">
        <div className="flex gap-6">
          {/* Left ad rail — paid sponsor slot, sticky */}
          <aside className="hidden xl:block w-[180px] shrink-0 xl:sticky xl:top-[200px] self-start" data-testid={`app-${config.appId}-adrail-left`}>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">Sponsored</div>
            <div className="space-y-3">
              <InlineAdBanner slot={`app-${config.appId}`} city={city} className="rounded-2xl" />
              <InlineAdBanner slot={`app-${config.appId}`} city={city} className="rounded-2xl" />
            </div>
          </aside>

          {/* Main column */}
          <div className="flex-1 min-w-0">
            {/* Active filter chips + mobile trigger */}
            <div className="mb-4 flex items-center gap-2 flex-wrap [scroll-margin-top:200px]" data-testid={`app-${config.appId}-active-filters`}>
              {(config.filterGroups || []).length > 0 && (
                <Sheet>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="lg:hidden rounded-full"
                      data-testid={`app-${config.appId}-filters-mobile`}
                    >
                      <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Filters{activeCount > 0 ? ` (${activeCount})` : ""}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="right" className="w-[320px] sm:w-[360px] overflow-y-auto">
                    <SheetHeader><SheetTitle>Filters</SheetTitle></SheetHeader>
                    <div className="mt-4">{renderFiltersBody()}</div>
                    {activeCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearAllFilters} className="mt-3 w-full">
                        <X className="h-3.5 w-3.5 mr-1" /> Clear all
                      </Button>
                    )}
                  </SheetContent>
                </Sheet>
              )}
              {Object.entries(selected).flatMap(([groupId, vals]) =>
                (vals || []).map((v) => (
                  <button
                    key={`${groupId}:${v}`}
                    onClick={() => toggleOption(groupId, v)}
                    className="inline-flex items-center gap-1 rounded-full bg-muted text-xs px-2.5 py-1 hover:bg-muted-foreground/20"
                    data-testid={`app-${config.appId}-chip-${groupId}-${v}`}
                  >
                    {v} <X className="h-3 w-3" />
                  </button>
                ))
              )}
              {activeCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-[11px] text-muted-foreground underline hover:text-foreground"
                  data-testid={`app-${config.appId}-filters-clear`}
                >
                  Clear all
                </button>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {filtered.length} {filtered.length === 1 ? "vendor" : "vendors"}
              </span>
            </div>

            {loading ? (
              <p className="text-center text-muted-foreground py-12">{t("vl.loading")}</p>
            ) : filtered.length === 0 ? (
              <Card className="p-10 border-border/60 text-center" data-testid={`app-${config.appId}-empty`}>
                <p className="text-muted-foreground">{t("apps.no_vendors")}</p>
                {activeCount > 0 && (
                  <Button variant="outline" className="mt-4 rounded-full" onClick={clearAllFilters}>
                    Clear filters
                  </Button>
                )}
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid={`app-${config.appId}-grid`}>
                {filtered.map((v, idx) => (
                  <React.Fragment key={v.vendor_id}>
                    {idx > 0 && idx % 6 === 0 && (
                      <div className="sm:col-span-2 xl:col-span-3" data-testid={`app-${config.appId}-grid-ad-${idx}`}>
                        <InlineAdBanner slot={`app-${config.appId}`} city={city} />
                      </div>
                    )}
                    <Card className="overflow-hidden border-border/60 transition-all hover:-translate-y-1 hover:shadow-xl flex flex-col" data-testid={`app-${config.appId}-card-${v.vendor_id}`}>
                    <Link to={`/vendors/${v.vendor_id}`} target="_blank" rel="noreferrer" className="block">
                      {v.banner ? (
                        <img src={v.banner} alt="" className="aspect-[16/9] w-full object-cover" />
                      ) : (
                        <div className="aspect-[16/9] w-full" style={{ background: `linear-gradient(135deg, ${config.theme.primary}33, ${config.theme.accent || config.theme.primary}66)` }} />
                      )}
                    </Link>
                    <div className="p-4 flex-1 flex flex-col">
                      <div className="flex items-start gap-3">
                        {v.logo && <img src={v.logo} alt="" className="h-10 w-10 rounded-xl object-cover -mt-9 ring-4 ring-background" />}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <h3 className="font-heading text-lg leading-tight truncate">{v.business_name}</h3>
                            {v.is_verified && <ShieldCheck className="h-3.5 w-3.5 text-primary shrink-0" />}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" /> {v.city} · {v.category}
                          </div>
                        </div>
                        {v.rating > 0 && (
                          <Badge variant="secondary" className="gap-1"><Star className="h-3 w-3 fill-current" />{Number(v.rating).toFixed(1)}</Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 line-clamp-2">{v.description}</p>
                      {v.products && v.products.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {v.products.slice(0, 3).map((p, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[10px]">
                              {(p.image || p.image_url) && (
                                <img src={p.image || p.image_url} alt="" className="h-3 w-3 rounded-full object-cover" />
                              )}
                              {p.name}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="mt-auto pt-3 flex gap-2">
                        <Button asChild size="sm" variant="outline" className="rounded-full flex-1" data-testid={`app-vendor-view-${v.vendor_id}`}>
                          <Link to={`/vendors/${v.vendor_id}`} target="_blank" rel="noreferrer">
                            {t("vl.btn_view")} <ExternalLink className="h-3 w-3 ml-1" />
                          </Link>
                        </Button>
                        <Button asChild size="sm" className="rounded-full flex-1" style={{ background: config.theme.primary }} data-testid={`app-vendor-chat-${v.vendor_id}`}>
                          <Link to={`/vendors/${v.vendor_id}?chat=1`}><MessageCircle className="h-3 w-3 mr-1" /> {t("vl.btn_chat")}</Link>
                        </Button>
                      </div>
                    </div>
                  </Card>
                  </React.Fragment>
                ))}
              </div>
            )}

            <div className="mt-8 flex justify-center">
              <Button asChild variant="outline" className="rounded-full" data-testid={`app-${config.appId}-view-all`}>
                <Link to={`/vendors?categories=${encodeURIComponent(config.categories.join(","))}${city ? `&city=${encodeURIComponent(city)}` : ""}`}>
                  {t("apps.view_all").replace("{name}", t(`apps.${config.appId}.name`))}
                  <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>

          {/* Right sidebar (desktop) */}
          {(config.filterGroups || []).length > 0 && (
            <aside className="hidden lg:block w-[260px] shrink-0 lg:sticky lg:top-[200px] self-start" data-testid={`app-${config.appId}-sidebar`}>
              <Card className="p-4 border-border/60">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading text-base flex items-center gap-1.5">
                    <SlidersHorizontal className="h-4 w-4" /> Filters
                  </h3>
                  {activeCount > 0 && (
                    <button onClick={clearAllFilters} className="text-[11px] text-muted-foreground underline hover:text-foreground" data-testid={`app-${config.appId}-sidebar-clear`}>
                      Clear all
                    </button>
                  )}
                </div>
                <ScrollArea className="max-h-[calc(100vh-200px)]">
                  {renderFiltersBody()}
                </ScrollArea>
              </Card>
            </aside>
          )}
        </div>
      </section>
    </div>
  );
}
